import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getCategories } from "../../api/categoryService";
import { useLanguage } from "../../context/LanguageContext";
import { pickLocalizedName } from "../../utils/localizedName";
import "./clientTheme.css";
import "./CategoryMegaMenu.css";

const OPEN_DELAY = 80;
const CLOSE_DELAY = 150;
const MAX_L3_PER_COLUMN = 8;
// Touch devices / narrow screens get a tap-to-open sheet instead of the hover panel.
const SHEET_MEDIA_QUERY = "(hover: none), (max-width: 720px)";

function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(query).matches
  );
  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    // Older Safari / in-app WebViews only support addListener.
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);
  return matches;
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function ArrowLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

export default function CategoryMegaMenu() {
  const { t } = useTranslation("categoryMegaMenu");
  const { language } = useLanguage();
  const [categories, setCategories] = useState(null);
  const [openL1, setOpenL1] = useState(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [sheetL1, setSheetL1] = useState(null);
  const isSheetMode = useMediaQuery(SHEET_MEDIA_QUERY);

  const navRef = useRef(null);
  const scrollRef = useRef(null);
  const closeTimerRef = useRef(null);
  const openTimerRef = useRef(null);
  const lastScrollYRef = useRef(0);
  const lastToggleAtRef = useRef(0);

  useEffect(() => {
    getCategories({}).then(setCategories).catch(() => setCategories([]));
  }, []);

  const level1 = useMemo(
    () => (categories || []).filter((c) => !c.parentCategoryId),
    [categories]
  );

  function childrenOf(categoryId) {
    return (categories || []).filter((c) => c.parentCategoryId === categoryId);
  }

  function updateScrollState() {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }

  useEffect(() => {
    updateScrollState();
    window.addEventListener("resize", updateScrollState);
    return () => window.removeEventListener("resize", updateScrollState);
  }, [level1]);

  useEffect(() => {
    return () => {
      clearTimeout(closeTimerRef.current);
      clearTimeout(openTimerRef.current);
    };
  }, []);

  useEffect(() => {
    lastScrollYRef.current = window.scrollY;
    function handleScroll() {
      const y = window.scrollY;
      const last = lastScrollYRef.current;
      const delta = y - last;
      if (Math.abs(delta) < 24) return;
      lastScrollYRef.current = y;

      const now = Date.now();
      if (now - lastToggleAtRef.current < 350) return;

      const shouldCollapse = y >= 60 && delta > 0;
      setCollapsed((prev) => {
        if (prev === shouldCollapse) return prev;
        lastToggleAtRef.current = now;
        return shouldCollapse;
      });
    }
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!sheetL1) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function handleKeyDown(e) {
      if (e.key === "Escape") setSheetL1(null);
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [sheetL1]);

  function scrollRow(direction) {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.7, behavior: "smooth" });
  }

  function cancelClose() {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }

  function cancelOpen() {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  }

  function scheduleClose() {
    cancelOpen();
    cancelClose();
    closeTimerRef.current = setTimeout(() => setOpenL1(null), CLOSE_DELAY);
  }

  function scheduleOpen(categoryId) {
    cancelClose();
    cancelOpen();
    setCollapsed(false);
    openTimerRef.current = setTimeout(() => setOpenL1(categoryId), OPEN_DELAY);
  }

  if (!categories || level1.length === 0) return null;

  const activeCategory = openL1 ? level1.find((c) => c.categoryId === openL1) : null;
  const activeColumns = openL1 ? childrenOf(openL1) : [];
  const sheetCategory = sheetL1 ? level1.find((c) => c.categoryId === sheetL1) : null;
  const sheetLevel2 = sheetL1 ? childrenOf(sheetL1) : [];

  function closeSheet() {
    setSheetL1(null);
  }

  function handleLevel1Click(e, l1, hasChildren) {
    if (!isSheetMode || !hasChildren) return;
    // On mobile, tapping a parent category opens its subcategories instead of navigating.
    e.preventDefault();
    cancelOpen();
    cancelClose();
    setOpenL1(null);
    setSheetL1(l1.categoryId);
  }

  return (
    <nav
      className={`category-mega-menu${collapsed ? " cmm-collapsed" : ""}`}
      aria-label={t("categoriesNavLabel")}
      ref={navRef}
      onMouseEnter={() => setCollapsed(false)}
    >
      <div className="cmm-collapse">
        <div className="cmm-collapse-inner">
          <div className="cmm-row">
            <div className="cmm-scroll" ref={scrollRef} onScroll={updateScrollState}>
              <ul className="cmm-level1-list">
                {level1.map((l1) => {
                  const hasChildren = childrenOf(l1.categoryId).length > 0;
                  return (
                    <li
                      key={l1.categoryId}
                      className={`cmm-level1-item${openL1 === l1.categoryId ? " cmm-level1-item-active" : ""}`}
                      onMouseEnter={isSheetMode ? undefined : () => (hasChildren ? scheduleOpen(l1.categoryId) : scheduleClose())}
                      onMouseLeave={isSheetMode ? undefined : scheduleClose}
                    >
                      <Link
                        to={`/products?categoryId=${l1.categoryId}`}
                        className="cmm-level1-link"
                        onClick={(e) => handleLevel1Click(e, l1, hasChildren)}
                      >
                        {pickLocalizedName(language, l1.name, l1.nameArabic, l1.nameHebrew)}
                        {hasChildren && <ChevronDownIcon />}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            {(canScrollLeft || canScrollRight) && (
              <div className="cmm-arrows">
                <button
                  type="button"
                  className="cmm-arrow"
                  onClick={() => scrollRow(-1)}
                  disabled={!canScrollLeft}
                  aria-label={t("showPreviousCategories")}
                >
                  <ArrowLeftIcon />
                </button>
                <button
                  type="button"
                  className="cmm-arrow"
                  onClick={() => scrollRow(1)}
                  disabled={!canScrollRight}
                  aria-label={t("showMoreCategories")}
                >
                  <ArrowRightIcon />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {!isSheetMode && activeCategory && activeColumns.length > 0 && (
        <div className="cmm-panel" onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
          <div className="cmm-panel-inner">
            <div className="cmm-panel-columns">
              {activeColumns.map((l2) => {
                const l3Items = childrenOf(l2.categoryId);
                const shown = l3Items.slice(0, MAX_L3_PER_COLUMN);
                const remaining = l3Items.length - shown.length;
                return (
                  <div className="cmm-column" key={l2.categoryId}>
                    <Link to={`/products?categoryId=${l2.categoryId}`} className="cmm-column-title">
                      {pickLocalizedName(language, l2.name, l2.nameArabic, l2.nameHebrew)}
                    </Link>
                    {shown.length > 0 && (
                      <ul className="cmm-column-list">
                        {shown.map((l3) => (
                          <li key={l3.categoryId}>
                            <Link to={`/products?categoryId=${l3.categoryId}`} className="cmm-column-link">
                              {pickLocalizedName(language, l3.name, l3.nameArabic, l3.nameHebrew)}
                            </Link>
                          </li>
                        ))}
                        {remaining > 0 && (
                          <li>
                            <Link to={`/products?categoryId=${l2.categoryId}`} className="cmm-column-link cmm-column-more">
                              {t("moreItems", { count: remaining })}
                            </Link>
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>

            <Link to={`/products?categoryId=${activeCategory.categoryId}`} className="cmm-feature">
              {activeCategory.imageUrl ? (
                <img src={activeCategory.imageUrl} alt="" className="cmm-feature-image" />
              ) : (
                <div className="cmm-feature-image cmm-feature-image-placeholder">
                  {pickLocalizedName(language, activeCategory.name, activeCategory.nameArabic, activeCategory.nameHebrew).charAt(0)}
                </div>
              )}
              <div className="cmm-feature-overlay">
                <span className="cmm-feature-label">
                  {t("shopAllCategory", {
                    categoryName: pickLocalizedName(language, activeCategory.name, activeCategory.nameArabic, activeCategory.nameHebrew),
                  })}
                </span>
                <span className="cmm-feature-cta">
                  <ArrowRightIcon />
                </span>
              </div>
            </Link>
          </div>
        </div>
      )}

      {sheetCategory &&
        createPortal(
          <div className="cmm-sheet-backdrop" onClick={closeSheet}>
            <div
              className="cmm-sheet"
              role="dialog"
              aria-modal="true"
              aria-label={pickLocalizedName(language, sheetCategory.name, sheetCategory.nameArabic, sheetCategory.nameHebrew)}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="cmm-sheet-handle" />
              <div className="cmm-sheet-header">
                <Link
                  to={`/products?categoryId=${sheetCategory.categoryId}`}
                  className="cmm-sheet-title"
                  onClick={closeSheet}
                >
                  {sheetCategory.imageUrl ? (
                    <img src={sheetCategory.imageUrl} alt="" className="cmm-sheet-title-image" />
                  ) : (
                    <span className="cmm-sheet-title-image cmm-feature-image-placeholder">
                      {pickLocalizedName(language, sheetCategory.name, sheetCategory.nameArabic, sheetCategory.nameHebrew).charAt(0)}
                    </span>
                  )}
                  <span className="cmm-sheet-title-text">
                    <span className="cmm-sheet-title-name">
                      {pickLocalizedName(language, sheetCategory.name, sheetCategory.nameArabic, sheetCategory.nameHebrew)}
                    </span>
                    <span className="cmm-sheet-title-cta">
                      {t("shopAllCategory", {
                        categoryName: pickLocalizedName(language, sheetCategory.name, sheetCategory.nameArabic, sheetCategory.nameHebrew),
                      })}
                      <ArrowRightIcon />
                    </span>
                  </span>
                </Link>
                <button type="button" className="cmm-sheet-close" onClick={closeSheet} aria-label={t("close")}>
                  <CloseIcon />
                </button>
              </div>

              <div className="cmm-sheet-body">
                {sheetLevel2.map((l2) => {
                  const l3Items = childrenOf(l2.categoryId);
                  return (
                    <section className="cmm-sheet-section" key={l2.categoryId}>
                      <Link
                        to={`/products?categoryId=${l2.categoryId}`}
                        className="cmm-sheet-section-title"
                        onClick={closeSheet}
                      >
                        <span>{pickLocalizedName(language, l2.name, l2.nameArabic, l2.nameHebrew)}</span>
                        <ArrowRightIcon />
                      </Link>
                      {l3Items.length > 0 && (
                        <div className="cmm-sheet-chips">
                          {l3Items.map((l3) => (
                            <Link
                              key={l3.categoryId}
                              to={`/products?categoryId=${l3.categoryId}`}
                              className="cmm-sheet-chip"
                              onClick={closeSheet}
                            >
                              {pickLocalizedName(language, l3.name, l3.nameArabic, l3.nameHebrew)}
                            </Link>
                          ))}
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body
        )}
    </nav>
  );
}
